import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getGetAllJobsQueryKey,
  useGetJobStatus,
} from '@/common/api/endpoints/jobs/jobs';
import { getGetModuleVersionQueryKey } from '@/common/api/endpoints/modules/modules';
import { useDialog } from '@/common/context/DialogContext';
import {
  useModuleJobStore,
  workingModeApplyJobKey,
} from './useModuleJobStore';

interface UseModuleWorkingModeApplyJobParams {
  moduleId: string | null | undefined;
  version: string | null | undefined;
  tasksQueryKey: readonly unknown[];
}

interface StoredJobStatus {
  id: string;
  status: string;
  currentStage?: string;
  progress?: number;
}

/**
 * Watches the working-mode apply job for the open module version for the full
 * Module Editor lifetime (settings, task tabs, remounts).
 *
 * Completion must live here — not only on WorkingModeBlock — so task caches
 * (oral question pool, essay rubrics) refresh even if the lecturer left
 * Module Settings before the job finished.
 */
export const useModuleWorkingModeApplyJob = ({
  moduleId,
  version,
  tasksQueryKey,
}: UseModuleWorkingModeApplyJobParams) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const queryClient = useQueryClient();

  const storeKey = moduleId && version ? workingModeApplyJobKey(moduleId, version) : null;
  const storedJobId = useModuleJobStore((state) => (
    storeKey ? state.workingModeApplyJobs[storeKey] : undefined
  ));
  const clearWorkingModeApplyJob = useModuleJobStore((state) => state.clearWorkingModeApplyJob);

  const completionHandledRef = useRef<string | null>(null);

  const {
    data: trackedJob,
    isError: isTrackedJobError,
    isPending: isTrackedJobPending,
    isFetched: isTrackedJobFetched,
  } = useGetJobStatus(storedJobId ?? null, undefined, {
    query: {
      enabled: !!storedJobId,
      retry: false,
      refetchInterval: (query) => {
        if (query.state.error) return false;
        const jobs = query.state.data?.data?.data as StoredJobStatus[] | undefined;
        const job = jobs?.[0];
        if (!job) return 5000;
        if (job.status === 'running' || job.status === 'pending') return 5000;
        return false;
      },
      select: (response) => {
        const jobs = response.data?.data as unknown as StoredJobStatus[] | undefined;
        return jobs?.[0];
      },
    },
  });

  const isJobActive = !!storedJobId && !isTrackedJobError && (
    isTrackedJobPending
    || trackedJob?.status === 'running'
    || trackedJob?.status === 'pending'
  );

  useEffect(() => {
    if (!moduleId || !version || !storedJobId) return;

    const vanished = isTrackedJobError
      || (isTrackedJobFetched && !isTrackedJobPending && !trackedJob);
    const terminal = trackedJob?.status === 'success' || trackedJob?.status === 'failed';

    if (!vanished && !terminal) return;
    if (completionHandledRef.current === storedJobId) return;

    completionHandledRef.current = storedJobId;
    clearWorkingModeApplyJob(moduleId, version);

    queryClient.invalidateQueries({ queryKey: tasksQueryKey });
    queryClient.invalidateQueries({
      queryKey: getGetModuleVersionQueryKey(moduleId, version),
    });
    queryClient.invalidateQueries({
      queryKey: [`/modules/${moduleId}/versions/${version}/working-mode/diff`],
    });
    queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({}) });
    queryClient.invalidateQueries({ queryKey: getGetAllJobsQueryKey({ completed: true }) });

    if (trackedJob?.status === 'success') {
      showToastMessage(
        'success',
        t('lecturer.moduleEditor.workingMode.messages.applySuccess'),
      );
    }
  }, [
    moduleId,
    version,
    storedJobId,
    trackedJob,
    isTrackedJobError,
    isTrackedJobFetched,
    isTrackedJobPending,
    clearWorkingModeApplyJob,
    queryClient,
    tasksQueryKey,
    showToastMessage,
    t,
  ]);

  return {
    isJobActive,
    storedJobId,
  };
};
